import { ReactNode, useCallback, useMemo, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { SwingPosition, Unit } from '@core';
import { useSaveSwingFoundationParameterSelection } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivityReport, useCategories, useUiNodes, useUiParameters } from '@queries';
import {
    activeUserSettingsAtom,
    activityIdAtom,
    activityUnitAtom,
    comparisonAnalysisAtom,
    convertedLayoutCustomCorridorsMapAtom,
    corridorsAtom,
    currentPositionNumberAtom,
    layoutCustomizationsAtom,
    selectedActiveAnalysisAtom,
    selectedSwingGuideParameterAtom,
    setGoalCorridorParameterAtom,
    swingFoundationParameterIdsAtom,
    swingGuideParametersAtom,
} from '@store';
import {
    Above,
    ActionButton,
    Box,
    Heading4,
    LayoutIcon,
    MenuItem,
    Only,
    SwingFoundationParameterCard,
    useAbove,
    useResizeObserver,
} from '@ui';

import { ModuleBase } from '../ModuleBase/ModuleBase';

import { ParameterTable } from './ParameterTable';
import * as css from './SwingFoundations.css';

const SCROLL_HEIGHT = 608;

type SwingFoundationsProps = {
    isEditing?: boolean;
    videoComponent?: ReactNode;
    navigateToEditor?: () => void;
};

export const SwingFoundations = ({ isEditing, navigateToEditor }: SwingFoundationsProps) => {
    const activityId = useAtomValue(activityIdAtom);
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const activePosition = useAtomValue(currentPositionNumberAtom);
    const activeSwingFoundationPosition = useMemo(
        // filter by enabled swing foundation positions
        () => [1, 2, 4, 6, 7, 8].reverse().find((pos) => pos <= activePosition),
        [activePosition],
    );

    const setGoalCorridorParameterId = useSetAtom(setGoalCorridorParameterAtom);
    const setSelectedGuideParameterId = useSetAtom(selectedSwingGuideParameterAtom);
    const { data: swingFoundations } = useUiNodes('swing_foundations');
    const { data: parameters } = useUiParameters();
    const swingGuideParameters = useAtomValue(swingGuideParametersAtom);
    const paramMap = useMemo(
        () => Object.fromEntries(parameters?.map((param) => [param.id, param] as const) || []),
        [parameters],
    );
    const { data: swingFoundationGroups } = useCategories('swing_foundation_group');
    const groupMap = useMemo(
        () => Object.fromEntries(swingFoundationGroups?.map((group) => [group.id, group] as const) || []),
        [swingFoundationGroups],
    );
    const customizations = useAtomValue(layoutCustomizationsAtom);
    const layoutCorridorsMap = useAtomValue(convertedLayoutCustomCorridorsMapAtom);
    const activeSwingAnalysis = useAtomValue(selectedActiveAnalysisAtom);
    const selectedSwingFoundationParameterIds = useAtomValue(swingFoundationParameterIdsAtom);
    const saveSwingFoundationParameterSelection = useSaveSwingFoundationParameterSelection();

    const comparisonSwingAnalysis = useAtomValue(comparisonAnalysisAtom);
    const { data: activityReport } = useActivityReport(activityId);

    const corridors = useAtomValue(corridorsAtom);

    const swingFoundationParameterIds = useMemo(() => {
        const allParameters = customizations
            ?.filter(
                (customization) =>
                    customization.parentUiNodeId?.includes('swing_foundations.default_kiosk') &&
                    customization.showChildOn?.includes('kiosk'),
            )
            .map((customization) => customization.childUiNodeId);

        return allParameters?.sort((paramId, nextParamId): number => {
            const paramGroup = groupMap[paramMap?.[paramId]?.categories.swingFoundationGroup || ''];
            const nextParamGroup = groupMap[paramMap?.[nextParamId]?.categories.swingFoundationGroup || ''];
            return paramGroup?.displayOrder - nextParamGroup?.displayOrder;
        });
    }, [customizations, groupMap, paramMap]);

    const removeParameterFromLayout = useCallback(
        async (parameterId: string) => {
            const parameterIds = selectedSwingFoundationParameterIds.filter((id) => id !== parameterId);
            await saveSwingFoundationParameterSelection(parameterIds);
        },
        [saveSwingFoundationParameterSelection, selectedSwingFoundationParameterIds],
    );

    const swingFoundationParameters = useMemo(() => {
        return parameters?.filter(
            ({ id, categories }) =>
                swingFoundationParameterIds?.includes(id) &&
                categories.swingFoundationGroup?.includes('p' + activeSwingFoundationPosition),
        );
    }, [activeSwingFoundationPosition, parameters, swingFoundationParameterIds]);

    const kioskSwingFoundations = swingFoundations?.find((node) => node.showOn.includes('kiosk'));

    const ref = useRef<HTMLDivElement>(null);

    const { height } = useResizeObserver({
        ref,
    });
    const isDesktop = useAbove('md');
    const referenceHeight = isDesktop ? height || SCROLL_HEIGHT : undefined;

    const hasParameters = swingFoundationParameters && swingFoundationParameters.length > 0;

    const t = useTranslate();

    const position = activeSwingFoundationPosition ? (('p' + activeSwingFoundationPosition) as SwingPosition) : null;
    const title = position ? t(`swing_position.${position}`) : '';

    return (
        <ModuleBase
            isStatic
            title={title}
            isClosed={isEditing}
            subtitle={
                <Box display="flex" alignItems="center">
                    <Heading4>
                        {kioskSwingFoundations?.i18n ? t(kioskSwingFoundations.i18n) : 'Swing Foundations'}
                    </Heading4>
                    {navigateToEditor && (
                        <Only bp="sm" spans={2}>
                            <ActionButton
                                icon="pencil"
                                variant="plain"
                                buttonSize="tiny"
                                iconSize="small"
                                onClick={navigateToEditor}
                            />
                        </Only>
                    )}
                </Box>
            }
            button={
                navigateToEditor ? (
                    <Above bp="lg">
                        <button
                            className={css.topButton({ selected: false, variant: 'secondary' })}
                            onClick={navigateToEditor}
                        >
                            <LayoutIcon width={16} />
                            <Heading4>Parameters</Heading4>
                        </button>
                    </Above>
                ) : null
            }
        >
            <div className={css.root}>
                <ParameterTable referenceHeight={referenceHeight}>
                    <div className={css.grid}>
                        {hasParameters ? (
                            swingFoundationParameters?.map((param) => {
                                const { id, parameter } = param;
                                const paramValue =
                                    activeSwingAnalysis?.data.analysis?.parameter_values?.[parameter.name];
                                const comparisonParamValue =
                                    comparisonSwingAnalysis?.fullAnalysis?.data.analysis?.parameter_values?.[
                                        parameter.name
                                    ];
                                const reportValue = activityReport?.[parameter.name];
                                const corridor = corridors?.[parameter.name];
                                const customCorridor = layoutCorridorsMap?.[parameter.smpId];
                                return (
                                    <SwingFoundationParameterCard
                                        key={id}
                                        title={t(param?.i18n ?? '-')}
                                        unit={parameter.unit as Unit}
                                        value={paramValue?.value}
                                        mean={reportValue?.mean}
                                        stdDev={reportValue?.stdDev}
                                        userPreferredUnit={activityUnit}
                                        corridorParameterMargins={userSettings.showCorridors ? corridor : null}
                                        goalCorridor={userSettings.showGoal ? customCorridor : null}
                                        comparisonValue={
                                            userSettings.showComparison ? comparisonParamValue?.value : null
                                        }
                                        menuItems={
                                            <>
                                                <MenuItem onClick={() => setGoalCorridorParameterId(param)}>
                                                    Add/adjust goal corridors
                                                </MenuItem>
                                                {swingGuideParameters.has(param.parameter.smpId) && (
                                                    <MenuItem
                                                        onClick={() => setSelectedGuideParameterId(parameter.smpId)}
                                                    >
                                                        Guide
                                                    </MenuItem>
                                                )}
                                                <MenuItem onClick={() => removeParameterFromLayout(id)}>
                                                    {t('layout_editor.remove_parameter')}
                                                </MenuItem>
                                            </>
                                        }
                                    />
                                );
                            })
                        ) : (
                            <div className={css.emptyMessage}>
                                <Heading4>No parameter configured for this P position</Heading4>
                            </div>
                        )}
                    </div>
                </ParameterTable>
            </div>
        </ModuleBase>
    );
};
